import {AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../shared/store/store';
import {DialogFormStore} from '../../../../../shared/store/dialog/dialogForm.store';
import {ManageConditionalDimensionDialogStore} from './manage-conditional-dimension-dialog.store';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {DimensionComponentObject, DimensionComponentObjects} from '../../../../../shared/classes/dimension-component-object';
import {SelectOption, SelectOptions} from '../../../../../shared/interfaces/form';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  ConditionalDimensionAction,
  ConditionalDimensionElseAction,
  ConditionalDimensionOperator,
  DataSetConditionalDimension,
  DataSetConditionalDimensionRule,
  DataSetConditionalDimensionRuleCondition
} from '../../../../../shared/interfaces/dimensions';
import {SettingsService} from '../../../../../shared/services/settings.service';
import {DimensionsService} from '../../../../../shared/services/dimensions.service';
import {Visibility} from '../../../../../shared/interfaces/settings';
import {
  createDataSetConditionalDimension,
  createDataSetConditionalDimensionSuccess,
  updateDataSetConditionalDimension,
  updateDataSetConditionalDimensionSuccess
} from '../../../../../shared/store/dataSetConditionalDimensions/dataSetConditionalDimensions.actions';
import {
  selectDataSetDimensionsGroupComponentObjects
} from '../../../../../shared/store/dataSetDimensionsGroups/dataSetDimensionsGroups.selectors';
import {
  DatasetDimensionsGroupComponentObject,
  DatasetDimensionsGroupComponentObjects
} from '../../../../../shared/classes/dataset-dimensions-group-component-object';
import {selectDimensionComponentObjectsWithoutDate} from '../../../../../shared/store/selectors';
import {DatasetConditionalDimensionComponentObject} from '../../../../../shared/classes/dataset-conditional-dimension-component-object';

@Component({
  selector: 'app-manage-conditional-dimension-popin',
  templateUrl: './manage-conditional-dimension-dialog.component.html',
  styleUrls: ['./manage-conditional-dimension-dialog.component.scss'],
  providers: [
    DialogFormStore,
    ManageConditionalDimensionDialogStore
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageConditionalDimensionDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly form: FormGroup<{
    name: FormControl<string>,
    visibility: FormControl<Visibility>,
    category: FormControl<string>,
    rules: FormArray<FormGroup<{
      groups: FormArray<FormGroup<{
        dimension: FormControl<DimensionComponentObject | null>,
        operator: FormControl<ConditionalDimensionOperator>,
        value: FormControl<string | null>,
        conditionOperator: FormControl<'or' | 'and'>,
        valid: FormControl<boolean>
      }>>,
      thenAction: FormControl<ConditionalDimensionAction>,
      then: FormControl<string | DimensionComponentObject | null>,
      valid: FormControl<boolean>,
    }>>,
    elseAction: FormControl<ConditionalDimensionElseAction>,
    else: FormControl<string | DimensionComponentObject | null>,
    valid: FormControl<boolean>
  }> = this._manageConditionalDimensionsDialogStore.form;

  public readonly operators: SelectOptions<ConditionalDimensionOperator, string> = this._dimensionService.operators;

  public readonly resultActions: SelectOptions<ConditionalDimensionAction, string> = [
    {key: 'fixed_value', text: 'result_actions.fixed_value'},
    {key: 'dimension_value', text: 'result_actions.dimension_value'}
  ];
  public readonly elseResultActions: SelectOptions<ConditionalDimensionElseAction, string> = [
    {key: 'fixed_value', text: 'result_actions.fixed_value'},
    {key: 'dimension_value', text: 'result_actions.dimension_value'},
    {key: 'no_value', text: 'result_actions.no_value'}
  ];

  public readonly categories$: Observable<DatasetDimensionsGroupComponentObjects> = this._store.select(selectDataSetDimensionsGroupComponentObjects)
    .pipe(
    map((groups: DatasetDimensionsGroupComponentObjects): DatasetDimensionsGroupComponentObjects => groups.map((group: DatasetDimensionsGroupComponentObject): DatasetDimensionsGroupComponentObject =>
        group.filterDimensions((dimension: DimensionComponentObject): boolean =>
          !['day', 'week', 'month', 'year', 'gran-code', 'ad-image', 'device', 'channel', 'channel-group'].includes(dimension.payload.attributes.slug) && dimension.payload.type !== 'data_set_conditional_dimension'
        ))
      )
    );
  public readonly dimensions$: Observable<DimensionComponentObjects> = this._store.select(selectDimensionComponentObjectsWithoutDate);

  public readonly dimensionsGroups$: Observable<DatasetDimensionsGroupComponentObjects> = this._store.select(selectDataSetDimensionsGroupComponentObjects);
  public readonly loading$: Observable<boolean> = this._popinStore.loading$;

  public readonly datasetConditionalDimension: DatasetConditionalDimensionComponentObject | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly _data: DatasetConditionalDimensionComponentObject | object,
    public readonly dialogRef: MatDialogRef<ManageConditionalDimensionDialogComponent>,
    public readonly settingsService: SettingsService,
    private readonly _dimensionService: DimensionsService,
    private readonly _store: Store<AppState>,
    private readonly _manageConditionalDimensionsDialogStore: ManageConditionalDimensionDialogStore,
    private readonly _popinStore: DialogFormStore
  ) {
    if ('payload' in _data) {
      this.datasetConditionalDimension = _data;
    }
  }

  ngOnInit(): void {
    this._popinStore.init(
      this.form,
      this.dialogRef,
      [
        createDataSetConditionalDimension,
        updateDataSetConditionalDimension
      ],
      [
        createDataSetConditionalDimensionSuccess,
        updateDataSetConditionalDimensionSuccess
      ]
    );

    if (this.datasetConditionalDimension) {
      const elseData = this.datasetConditionalDimension.payload.attributes.else;
      const elseEntry = Array.isArray(elseData) && elseData.length > 0 ? elseData[0] : null;

      if (elseEntry) {
        this.form.controls.else.setValue(
          elseEntry.action === 'dimension_value' ? this.findDimensionBySlug(elseEntry.value) : elseEntry.value
        );
        this.form.controls.elseAction.setValue(elseEntry.action);
      } else {
        this.form.controls.else.setValue(null);
        this.form.controls.elseAction.setValue('no_value');
      }
    }

    this.form.controls.else.updateValueAndValidity();
    this.form.controls.elseAction.updateValueAndValidity();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {}

  public optionsTextGetter(option: SelectOption<ConditionalDimensionAction, string>): string {
    return option.text;
  }

  public optionsValueGetter(option: SelectOption<ConditionalDimensionAction, string>): ConditionalDimensionAction {
    return option.key;
  }

  public groupTextGetter(option: DatasetDimensionsGroupComponentObject): string {
    return option.payload.attributes.name;
  }

  public groupValueGetter(option: DatasetDimensionsGroupComponentObject): number {
    return option.payload.id;
  }

  public ruleOperatorGetter(option: SelectOption<ConditionalDimensionOperator, string>): ConditionalDimensionOperator {
    return option.key;
  }

  public onCreate(): void {
    this._store.dispatch(createDataSetConditionalDimension({
      dimension: this.formatParameters(this.form.value as unknown as {
        name: string,
        visibility: Visibility,
        category: string,
        rules: Array<{
          groups: Array<{
            dimension: DimensionComponentObject,
            operator: ConditionalDimensionOperator,
            value: string,
            conditionOperator: 'or' | 'and'
          }>,
          thenAction: ConditionalDimensionAction,
          then: DimensionComponentObject | string
        }>,
        elseAction: ConditionalDimensionAction,
        else: DimensionComponentObject | string | null
      })
    }));
  }

  public onModify(): void {
    this._store.dispatch(updateDataSetConditionalDimension({
      dimension: this.datasetConditionalDimension.payload,
      update: this.formatParameters(this.form.value as unknown as {
        name: string,
        visibility: Visibility,
        category: string,
        rules: Array<{
          groups: Array<{
            dimension: DimensionComponentObject,
            operator: ConditionalDimensionOperator,
            value: string,
            conditionOperator: 'or' | 'and'
          }>,
          thenAction: ConditionalDimensionAction,
          then: DimensionComponentObject | string
        }>,
        elseAction: ConditionalDimensionAction,
        else: DimensionComponentObject | string | null
      })
    }));
  }

  public addCondition(): void {
    this.form.controls.rules.push(new FormGroup({
      groups: new FormArray([
        new FormGroup<any>({
          dimension: new FormControl<DimensionComponentObject | null>(null, [Validators.required]),
          operator: new FormControl<ConditionalDimensionOperator>('equals', [Validators.required]),
          value: new FormControl<string | null>(null, [Validators.required]),
          conditionOperator: new FormControl<'or' | 'and'>('or'),
          valid: new FormControl(true)
        })
      ]),
      thenAction: new FormControl<ConditionalDimensionAction>('fixed_value', [Validators.required]),
      then: new FormControl<DimensionComponentObject | string | null>(null, [Validators.required]),
      valid: new FormControl(true)
    }));
  }

  public removeCondition(conditionFormGroup: FormGroup<{
    groups: FormArray<FormGroup<{
      dimension: FormControl<DimensionComponentObject | null>
      operator: FormControl<ConditionalDimensionOperator>
      value: FormControl<string | null>,
      conditionOperator: FormControl<'or' | 'and'>
    }>>,
    thenAction: FormControl<ConditionalDimensionAction>,
    then: FormControl<DimensionComponentObject | string | null>
  }>): void {
    this.form.controls.rules.removeAt(
      this.form.controls.rules.controls.findIndex((formGroup: FormGroup): boolean => formGroup === conditionFormGroup)
    );
  }

  public addAndConditionRule(
    conditionFormGroup: FormGroup<{
      groups: FormArray<FormGroup<{
        dimension: FormControl<DimensionComponentObject | null>
        operator: FormControl<ConditionalDimensionOperator>
        value: FormControl<string | null>,
        conditionOperator: FormControl<'or' | 'and'>,
        valid: FormControl<boolean>
      }>>,
      thenAction: FormControl<ConditionalDimensionAction>,
      then: FormControl<DimensionComponentObject | string | null>
    }>,
    groupForm: FormGroup<{
      dimension: FormControl<DimensionComponentObject | null>
      operator: FormControl<ConditionalDimensionOperator>
      value: FormControl<string | null>,
      conditionOperator: FormControl<'or' | 'and'>,
      valid: FormControl<boolean>
    }>
  ): void {
    conditionFormGroup.controls.groups.insert(
      conditionFormGroup.controls.groups.controls.findIndex((form: FormGroup): boolean => form === groupForm) + 1,
      new FormGroup({
        dimension: new FormControl<DimensionComponentObject | null>(null, [Validators.required]),
        operator: new FormControl<ConditionalDimensionOperator>('equals', [Validators.required]),
        value: new FormControl<string | null>(null, [Validators.required]),
        conditionOperator: new FormControl<'or' | 'and'>('and'),
        valid: new FormControl(true)
      })
    );
  }

  public addOrConditionRule(
    conditionFormGroup: FormGroup<{
      groups: FormArray<FormGroup<{
        dimension: FormControl<DimensionComponentObject | null>
        operator: FormControl<ConditionalDimensionOperator>
        value: FormControl<string | null>,
        conditionOperator: FormControl<'or' | 'and'>,
        valid: FormControl<boolean>
      }>>,
      thenAction: FormControl<ConditionalDimensionAction>,
      then: FormControl<DimensionComponentObject | string | null>
    }>
  ): void {
    conditionFormGroup.controls.groups.push(
      new FormGroup({
        dimension: new FormControl<DimensionComponentObject | null>(null, [Validators.required]),
        operator: new FormControl<ConditionalDimensionOperator>('equals', [Validators.required]),
        value: new FormControl<string | null>(null, [Validators.required]),
        conditionOperator: new FormControl<'or' | 'and'>('or'),
        valid: new FormControl(true)
      })
    );
  }

  public removeConditionRule(
    conditionFormGroup: FormGroup<{
      groups: FormArray<FormGroup<{
        dimension: FormControl<DimensionComponentObject | null>
        operator: FormControl<ConditionalDimensionOperator>
        value: FormControl<string | null>,
        conditionOperator: FormControl<'or' | 'and'>
      }>>,
      thenAction: FormControl<ConditionalDimensionAction>,
      then: FormControl<DimensionComponentObject | string | null>
    }>,
    groupForm: FormGroup<{
      dimension: FormControl<DimensionComponentObject | null>
      operator: FormControl<ConditionalDimensionOperator>
      value: FormControl<string | null>,
      conditionOperator: FormControl<'or' | 'and'>
    }>
  ): void {
    conditionFormGroup.controls.groups.removeAt(
      conditionFormGroup.controls.groups.controls.findIndex(form => form === groupForm)
    );

    if (!conditionFormGroup.controls.groups.value.length) {
      this.removeCondition(conditionFormGroup);
    }
  }

  private formatParameters(formValues: {
    name: string,
    visibility: Visibility,
    category: string,
    rules: Array<{
      groups: Array<{
        dimension: DimensionComponentObject,
        operator: ConditionalDimensionOperator,
        value: string,
        conditionOperator: 'or' | 'and'
      }>,
      thenAction: ConditionalDimensionAction,
      then: DimensionComponentObject | string
    }>,
    elseAction: ConditionalDimensionElseAction,
    else: DimensionComponentObject | string
  }): Partial<DataSetConditionalDimension> {
    return {
      data_set_dimensions_group_id: formValues.category,
      name: formValues.name,
      rules: formValues.rules.reduce((output: Array<DataSetConditionalDimensionRule>, current: {
        groups: Array<{
          dimension: DimensionComponentObject,
          operator: ConditionalDimensionOperator,
          value: string,
          conditionOperator: 'and' | 'or'
        }>,
        thenAction: ConditionalDimensionAction,
        then: DimensionComponentObject | string
      }) => [
        ...output,
        {
          rules: current.groups.reduce((output: Array<Array<DataSetConditionalDimensionRuleCondition>>, currentValue: {
            dimension: DimensionComponentObject,
            operator: ConditionalDimensionOperator,
            value: string,
            conditionOperator: 'and' | 'or'
          }) => {
            const rule: DataSetConditionalDimensionRuleCondition = {
              dimension: currentValue.dimension.payload.attributes.slug,
              operator: currentValue.operator,
              value: currentValue.value
            };

            if (currentValue.conditionOperator === 'or') {
              output.push([ rule ]);
            } else {
              output[output.length - 1].push(rule);
            }

            return output;
          }, [] as Array<Array<DataSetConditionalDimensionRuleCondition>>),
          thenAction: current.thenAction,
          then: current.thenAction === 'fixed_value' ? (current.then as string).trim() : (current.then as DimensionComponentObject).payload.attributes.slug
        }
      ], []),
      visibility: formValues.visibility,
      else: [{
        action: formValues.elseAction,
        value: (formValues.elseAction === 'no_value') ? null : (formValues.elseAction === 'fixed_value' ? (formValues.else as string).trim() : (formValues.else as DimensionComponentObject).payload.attributes.slug)
      }]
    };
  }

  public onElseSelect(control: FormControl<any>, elseControl: FormControl<DimensionComponentObject | string | null>): void {
    const elseCtrl = this.form.controls.else;
    if (control.value !== 'no_value') {
      elseCtrl.setValidators([Validators.required]);
    } else {
      elseCtrl.clearValidators();
    }

    // Recalculate validators and emit events so form validity updates under OnPush
    elseCtrl.updateValueAndValidity({ emitEvent: true });

    // Reset the else control and emit change so UI updates
    this.resetDimension(elseControl);
  }

  public resetDimension(control: FormControl<DimensionComponentObject | string | null>): void {
    // ensure the change is emitted so parent form and OnPush change detection react
    control.setValue(null, { emitEvent: true });
    control.markAsPristine();
    control.markAsUntouched();
    control.updateValueAndValidity({ emitEvent: true });

    // also ensure parent form validity is recalculated and emitted
    try {
      this.form.updateValueAndValidity({ emitEvent: true });
    } catch (e) {
      // noop
    }
  }

  private findDimensionBySlug(slug: string): DimensionComponentObject | null {
    let matchingDimension: DimensionComponentObject | null = null;
    this.dimensions$.subscribe(dimensions => {
      matchingDimension = dimensions.find(dimension => dimension.payload.attributes.slug === slug) || null;
    });
    return matchingDimension;
  }
}
